import { ReferralStatus } from "@/lib/schema";
import nextStepIcon from "@/assets/referral-timeline/interviewing.svg";
import completedIcon from "@/assets/referral-timeline/completed.svg";

interface ReferralProgressTimelineProps {
  status: ReferralStatus;
}

export function ReferralProgressTimeline({ status }: ReferralProgressTimelineProps) {
  // Map referral statuses to timeline steps (0-indexed for easier calculation)
  const getStepFromStatus = (currentStatus: ReferralStatus): number => {
    switch (currentStatus) {
      case ReferralStatus.PENDING:
      case ReferralStatus.SUBMITTED:
        return 1; // Step 1: Submitted
      case ReferralStatus.SHORTLISTED:
      case ReferralStatus.INTERVIEWED:
        return 2; // Step 2: Shortlisted/Interviewed
      case ReferralStatus.UNDER_REVIEW:
        return 3; // Step 3: Under Review
      case ReferralStatus.HIRED:
        return 4; // Step 4: Hired
      case ReferralStatus.PASSED_PROBATION:
        return 5; // Step 5: Passed Probation (final step)
      case ReferralStatus.REJECTED:
        return 2; // Default to step 2 for rejected
      default:
        return 1;
    }
  };

  const getStepStatus = (stepIndex: number, currentStatus: ReferralStatus) => {
    const currentStepIndex = getStepFromStatus(currentStatus);
    
    if (stepIndex < currentStepIndex) {
      return 'completed';
    } else if (stepIndex === currentStepIndex) {
      return 'current';
    } else {
      return 'pending';
    }
  };

  const steps = [
    { id: 1, title: "Submitted", date: "" },
    { id: 2, title: "Interviewed", date: "" },
    { id: 3, title: "Shortlisted", date: "" },
    { id: 4, title: "Hired", date: "" },
    { id: 5, title: "Passed Probation", date: "" }
  ];

  // Create the 10-column grid items: icon, line, icon, line, icon, line, icon, line, icon
  const createProgressGridItems = () => {
    const items = [];
    
    for (let i = 0; i < 5; i++) {
      const stepStatus = getStepStatus(i, status);
      const isCompleted = stepStatus === 'completed';
      const isCurrent = stepStatus === 'current';
      
      // Add step icon
      items.push(
        <div key={`step-${i}`} className="flex items-center justify-center">
          {isCompleted ? (
            <img 
              src={completedIcon} 
              alt="Completed" 
              className="w-5 h-5 sm:w-6 sm:h-6"
            />
          ) : isCurrent ? (
            <img 
              src={nextStepIcon} 
              alt="Current Step" 
              className="w-5 h-5 sm:w-6 sm:h-6"
            />
          ) : (
            <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-gray-300 rounded-full bg-white"></div>
          )}
        </div>
      );
      
      // Add connecting line (except after the last step)
      if (i < 4) {
        const currentStepCompleted = stepStatus === 'completed';
        const nextStepStatus = getStepStatus(i + 1, status);
        const nextStepExists = nextStepStatus === 'completed' || nextStepStatus === 'current';
        
        items.push(
          <div key={`line-${i}`} className="flex items-center justify-center h-full -mx-10 md:-mx-[30px] lg:-mx-10">
            <div 
              className={`w-full h-0.5 ${
                currentStepCompleted && nextStepExists && nextStepStatus === 'completed'
                  ? 'bg-[#008438]' // Solid green line only between completed steps
                  : currentStepCompleted && (nextStepStatus === 'current' || nextStepStatus === 'pending')
                  ? 'border-t border-dashed' // Dashed line from completed to next/pending step
                  : 'bg-gray-200' // Gray line for pending connections
              }`}
              style={{
                ...(currentStepCompleted && (nextStepStatus === 'current' || nextStepStatus === 'pending') && {
                  borderColor: '#D1D1D1',
                  backgroundColor: 'transparent'
                })
              }}
            />
          </div>
        );
      }
    }
    
    return items;
  };

  const currentStepIndex = getStepFromStatus(status);
  const currentStep = steps[currentStepIndex - 1]; // Current step based on status
  const nextStep = steps[currentStepIndex]; // Next step after current
  const completedSteps = steps.filter((_, index) => getStepStatus(index, status) === 'completed');

  return (
    <div className="relative py-4 sm:py-6 md:py-8 lg:py-10">
      <div className="relative px-4 sm:px-6 md:px-8 lg:px-10">
        {/* Mobile Progress View - Card-based design */}
        <div className="block sm:hidden">
          {/* Progress Summary */}
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center justify-between mb-3">
              <p 
                className="text-sm font-semibold space-grotesk leading-[180%]"
                style={{ 
                  color: '#6D6D6D',
                  letterSpacing: '0.28px'
                }}
              >
                Progress
              </p>
              <p 
                className="text-sm font-bold space-grotesk leading-[140%]"
                style={{ 
                  color: '#2D2D2D',
                  letterSpacing: '0.28px'
                }}
              >
                Step {currentStepIndex}/{steps.length}
              </p>
            </div>
            <div className="w-full rounded-full h-1.5" style={{ backgroundColor: '#EAEAEA' }}>
              <div 
                className="h-1.5 rounded-full transition-all duration-300"
                style={{ 
                  backgroundColor: '#008438',
                  width: `${(currentStepIndex / steps.length) * 100}%` 
                }}
              />
            </div>
          </div>

          {/* Current Step Card */}
          {currentStep && (
            <div 
              className="rounded p-4 sm:p-6 mb-4 sm:mb-6"
              style={{ 
                background: '#FFF',
                border: status === ReferralStatus.REJECTED 
                  ? '2px solid #E7A098' 
                  : '2px solid #008438'
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center">
                  {status === ReferralStatus.REJECTED ? (
                    <svg width="20" height="20" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path opacity="0.2" d="M14 8.5C14 9.68669 13.6481 10.8467 12.9888 11.8334C12.3295 12.8201 11.3925 13.5892 10.2961 14.0433C9.19975 14.4974 7.99335 14.6162 6.82946 14.3847C5.66558 14.1532 4.59648 13.5818 3.75736 12.7426C2.91825 11.9035 2.3468 10.8344 2.11529 9.67054C1.88378 8.50666 2.0026 7.30026 2.45673 6.2039C2.91085 5.10754 3.67989 4.17047 4.66658 3.51118C5.65328 2.85189 6.81331 2.5 8 2.5C9.5913 2.5 11.1174 3.13214 12.2426 4.25736C13.3679 5.38258 14 6.9087 14 8.5Z" fill="#E7A098"/>
                      <path d="M10.3538 6.85375L8.70688 8.5L10.3538 10.1462C10.4002 10.1927 10.4371 10.2479 10.4622 10.3086C10.4873 10.3692 10.5003 10.4343 10.5003 10.5C10.5003 10.5657 10.4873 10.6308 10.4622 10.6914C10.4371 10.7521 10.4002 10.8073 10.3538 10.8538C10.3073 10.9002 10.2521 10.9371 10.1915 10.9622C10.1308 10.9873 10.0657 11.0003 10 11.0003C9.93431 11.0003 9.86925 10.9873 9.80855 10.9622C9.74786 10.9371 9.69271 10.9002 9.64625 10.8538L8 9.20687L6.35375 10.8538C6.3073 10.9002 6.25215 10.9371 6.19145 10.9622C6.13075 10.9873 6.0657 11.0003 6 11.0003C5.93431 11.0003 5.86925 10.9873 5.80855 10.9622C5.74786 10.9371 5.69271 10.9002 5.64625 10.8538C5.5998 10.8073 5.56295 10.7521 5.53781 10.6914C5.51266 10.6308 5.49972 10.5657 5.49972 10.5C5.49972 10.4343 5.51266 10.3692 5.53781 10.3086C5.56295 10.2479 5.5998 10.1927 5.64625 10.1462L7.29313 8.5L5.64625 6.85375C5.55243 6.75993 5.49972 6.63268 5.49972 6.5C5.49972 6.36732 5.55243 6.24007 5.64625 6.14625C5.74007 6.05243 5.86732 5.99972 6 5.99972C6.13268 5.99972 6.25993 6.05243 6.35375 6.14625L8 7.79313L9.64625 6.14625C9.69271 6.09979 9.74786 6.06294 9.80855 6.0378C9.86925 6.01266 9.93431 5.99972 10 5.99972C10.0657 5.99972 10.1308 6.01266 10.1915 6.0378C10.2521 6.06294 10.3073 6.09979 10.3538 6.14625C10.4002 6.1927 10.4371 6.24786 10.4622 6.30855C10.4873 6.36925 10.5003 6.4343 10.5003 6.5C10.5003 6.5657 10.4873 6.63075 10.4622 6.69145C10.4371 6.75214 10.4002 6.8073 10.3538 6.85375ZM14.5 8.5C14.5 9.78558 14.1188 11.0423 13.4046 12.1112C12.6903 13.1801 11.6752 14.0132 10.4874 14.5052C9.29973 14.9972 7.99279 15.1259 6.73192 14.8751C5.47104 14.6243 4.31285 14.0052 3.40381 13.0962C2.49477 12.1872 1.8757 11.029 1.6249 9.76809C1.37409 8.50721 1.50282 7.20028 1.99479 6.01256C2.48676 4.82484 3.31988 3.80968 4.3888 3.09545C5.45772 2.38122 6.71442 2 8 2C9.72335 2.00182 11.3756 2.68722 12.5942 3.90582C13.8128 5.12441 14.4982 6.77665 14.5 8.5ZM13.5 8.5C13.5 7.4122 13.1774 6.34883 12.5731 5.44436C11.9687 4.53989 11.1098 3.83494 10.1048 3.41866C9.09977 3.00238 7.9939 2.89346 6.92701 3.10568C5.86011 3.3179 4.8801 3.84172 4.11092 4.61091C3.34173 5.3801 2.8179 6.36011 2.60568 7.427C2.39347 8.4939 2.50238 9.59977 2.91867 10.6048C3.33495 11.6098 4.0399 12.4687 4.94437 13.0731C5.84884 13.6774 6.91221 14 8 14C9.45819 13.9983 10.8562 13.4184 11.8873 12.3873C12.9184 11.3562 13.4983 9.95818 13.5 8.5Z" fill="#E7A098"/>
                    </svg>
                  ) : (
                    <img 
                      src={completedIcon} 
                      alt="Current Step" 
                      className="w-5 h-5 sm:w-6 sm:h-6"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <p 
                    className="text-xs font-semibold space-grotesk leading-[180%] mb-1 uppercase"
                    style={{ 
                      color: status === ReferralStatus.REJECTED ? '#E7A098' : '#008438',
                      letterSpacing: '0.28px'
                    }}
                  >
                    {status === ReferralStatus.REJECTED ? 'Rejected' : 'Current Step'}
                  </p>
                  <p 
                    className="text-base font-semibold space-grotesk leading-[130%]"
                    style={{ 
                      color: '#2D2D2D'
                    }}
                  >
                    {status === ReferralStatus.REJECTED ? 'Rejected' : currentStep.title}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Next Step Preview */}
          {nextStep && currentStepIndex < steps.length && status !== ReferralStatus.REJECTED && (
            <div 
              className="rounded p-4 sm:p-6"
              style={{ 
                background: '#FBFBFB',
                border: '1px solid #EAEAEA'
              }}
            >
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                  <img 
                    src={nextStepIcon} 
                    alt="Next Step" 
                    className="w-4 h-4 sm:w-5 sm:h-5"
                  />
                </div>
                <div className="flex-1">
                  <p 
                    className="text-xs font-semibold space-grotesk leading-[180%] mb-1 uppercase"
                    style={{ 
                      color: '#6D6D6D',
                      letterSpacing: '0.28px'
                    }}
                  >
                    Next Step
                  </p>
                  <p 
                    className="text-sm font-medium space-grotesk leading-[180%]"
                    style={{ 
                      color: '#2D2D2D',
                      letterSpacing: '0.28px'
                    }}
                  >
                    {steps[currentStepIndex]?.title || 'Complete'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Desktop/Tablet Timeline View */}
        <div className="hidden sm:block w-full">
          {/* 9-column progress grid with very tight spacing */}
          <div className="grid grid-cols-9 gap-0 items-center mb-4 w-full" style={{ gridTemplateColumns: '1fr 20px 1fr 20px 1fr 20px 1fr 20px 1fr' }}>
            {createProgressGridItems()}
          </div>
          
          {/* Labels positioned directly under icons */}
          <div className="grid grid-cols-9 gap-0 w-full" style={{ gridTemplateColumns: '1fr 20px 1fr 20px 1fr 20px 1fr 20px 1fr' }}>
            {steps.map((step, index) => {
              const stepStatus = getStepStatus(step.id - 1, status);
              const isCompleted = stepStatus === 'completed';
              const isCurrent = stepStatus === 'current';
              
              return (
                <div key={step.id} className="flex justify-center" style={{ gridColumnStart: index * 2 + 1, gridColumnEnd: index * 2 + 2 }}>
                  <div className="text-center">
                    <p className={`text-xs sm:text-sm font-medium space-grotesk leading-tight mb-1 ${
                      isCompleted ? 'text-gray-500' : 
                      isCurrent ? 'text-black' : 'text-gray-500'
                    }`}>
                      {step.id}
                    </p>
                    <p className={`text-xs sm:text-sm font-medium space-grotesk leading-tight ${
                      isCompleted ? 'text-gray-500' : 
                      isCurrent ? 'text-black' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                    {step.date && (
                      <p className="text-xs text-gray-400 space-grotesk mt-1">{step.date}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
